import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { combine, getQueryString } from 'src/app/shared/utilities/route';
import { ContractRequest } from 'src/app/shared/lib/contract-request/models/contract-request.model';
import { Margin } from 'src/app/shared/models/margin.model';
import { map } from 'rxjs/operators';
import { ContractRequestEdit } from './models/contract-request-edit.model';
import { ContractRequestAttachment } from './models/contract-request-attachment.model';
import { TypeGroupings } from 'src/app/shared/models/enums/type-groupings.enum';
import { KeyValue } from 'src/app/shared/models/key-value.model';
import { Reason } from 'src/app/shared/models/reason.model';
import { ContractRequestAdd } from './models/contract-request-add.model';
import { MarginProperties } from '../../models/margin-properties.model';
import { MarginDifference } from 'src/app/shared/lib/contract-request/models/contract-request-margin-differences.model';
import { MarginEffectiveDateEditDto } from './models/margin-effective-date.model';
import { FetchArgs } from '../../models/core';
import { interpolateParams } from '../../utilities/interpolate-params';
import { ContractRequestRule, GetChangeTypeResponseModel } from './models';

@Injectable({
    providedIn: 'root'
})
export class ContractRequestService {
    private readonly coreApiPath: string;
    private readonly intranetApiPath: string;

    constructor(
        private readonly http: HttpClient,
        @Inject(APP_CONFIG) settings: Settings
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova');
        this.intranetApiPath = combine(settings.coreAPIUrl, 'intranet');
    }

    getContractRequestById(id: number): Observable<ContractRequest> {
        return this.http.get<ContractRequest>(`${this.coreApiPath}/recruiting/contract-requests/${id}`).pipe(
            map((contractRequest) => {
                // Convert date fields to Date objects
                if (contractRequest.dateTo) {
                    contractRequest.dateTo = new Date(contractRequest.dateTo);
                }
                if (contractRequest.dateFrom) {
                    contractRequest.dateFrom = new Date(contractRequest.dateFrom);
                }
                return contractRequest;
            })
        );
    }

    getMarginById(id: number): Observable<Margin> {
        return this.http.get<any>(`${this.coreApiPath}/recruiting/margins?id=${id}`).pipe(map((x) => x.data[0]));
    }

    getUserId(): Observable<number> {
        return this.http.get<number>(`${this.coreApiPath}/contract-requests/userid`);
    }

    updateContractRequest(id: number, contractRequestEdit: ContractRequestEdit): Observable<object> {
        return this.http.put(`${this.coreApiPath}/contract-requests/${id}`, contractRequestEdit);
    }

    addContractRequest(data: ContractRequestAdd): Observable<number> {
        return this.http.post<number>(`${this.coreApiPath}/contract-requests`, data);
    }

    submitContractFileForRejectOrComplete(data: FormData) {
        return this.http.post(`${this.intranetApiPath}/contract-requests/files`, data);
    }

    submitChangeOfContractWithMultipleFiles(data: FormData): Observable<number[]> {
        return this.http.post<number[]>(`${this.intranetApiPath}/contract-requests/change-of-contract/files`, data);
    }

    uploadFileForContractRequest(data: FormData): Observable<ContractRequestAttachment> {
        return this.http.post<ContractRequestAttachment>(`${this.coreApiPath}/contract-requests/files`, data);
    }

    deleteAttachment(id: number): Observable<void> {
        return this.http.delete<void>(`${this.coreApiPath}/contract-requests/files/${id}`);
    }

    getAttachmentToPreview(id: number): Observable<any> {
        return this.http.get(`${this.coreApiPath}/contract-requests/files/${id}/attachment-preview`, {
            responseType: 'blob'
        });
    }

    download(id: number): Observable<any> {
        return this.http.get(`${this.coreApiPath}/contract-requests/files/${id}`, { responseType: 'blob' });
    }

    getMarginProperties(id: number): Observable<MarginProperties> {
        return this.http.get<any>(`${this.coreApiPath}/recruiting/margin-properties?id=${id}`);
    }

    getChangeType(candidateId: number, marginId: number): Observable<GetChangeTypeResponseModel> {
        return this.http.get<any>(
            `${this.coreApiPath}/contract-requests/types?candidateId=${candidateId}&marginId=${marginId}`
        );
    }

    getContractRequestChangeType(
        candidateId: number,
        facilityId: number,
        startDate: Date,
        endDate: Date
    ): Observable<GetChangeTypeResponseModel> {
        const url = combine(this.coreApiPath, 'contract-requests/change-type');
        const params = interpolateParams({ candidateId, facilityId, startDate, endDate });
        return this.http.get<GetChangeTypeResponseModel>(url, { params });
    }

    getReasons(typeGrouping: TypeGroupings): Observable<KeyValue<number, string>[]> {
        const url = `${this.coreApiPath}/contract-requests/reasons/${typeGrouping}`;
        const items = this.http.get<Reason[]>(url);
        return items.pipe(
            map((data) => {
                return data.map((element) => {
                    return { key: element.id, name: element.value };
                });
            })
        );
    }

    sendEmail(formData: FormData, contractRequestId: number): Observable<void> {
        const url = `${this.coreApiPath}/contract-requests/${contractRequestId}/email`;
        return this.http.post<void>(url, formData);
    }

    processContractChange(id: number, marginId: number, previousMarginId: number): Observable<void> {
        const url = `${this.coreApiPath}/nurse-contracts/contract-queue/process-contract-change`;
        return this.http.put<void>(`${url}/${id}?marginId=${marginId}&previousMarginId=${previousMarginId}`, null);
    }

    getDefaultContractRequestRules(marginItem: any): Observable<ContractRequestRule[]> {
        let url = `${this.coreApiPath}/contract-requests/rule-types`;
        url += getQueryString(marginItem);
        const items = this.http.get<any>(url);
        return items.pipe(
            map((data) => {
                return data.map((element) => {
                    return {
                        approver: element.approver,
                        contractRequestRuleTypeId: element.id,
                        isApproved: element.isApproved,
                        ruleName: element.name,
                        title: element.title,
                        attachmentsCount: element.attachmentsCount ?? 0,
                        ncNote: element.ncNote,
                        changeFields: element.changeFields,
                        attachments: [],
                        isSuppressed: element.isSuppressed
                    };
                });
            })
        );
    }

    getMarginDifferences(previousMarginId: number, marginId: number): Observable<MarginDifference[]> {
        const url = `${this.coreApiPath}/contract-requests/getMarginDifferences`;
        const params = interpolateParams({ previousMarginId, marginId });
        return this.http.get<MarginDifference[]>(url, { params });
    }

    saveMarginEffectiveDates(changes: MarginEffectiveDateEditDto[]): Observable<object> {
        return this.http.post(`${this.coreApiPath}/contract-requests/saveMarginEffectiveDates`, changes);
    }

    getContractRequestCount(args: FetchArgs): Observable<number> {
        let url = `${this.coreApiPath}/recruiting/contract-requests-count`;

        if (args.filter) {
            url += getQueryString(args.filter);
        }

        return this.http.get<number>(url);
    }

    formatDateRange(dateArray: string): string {
        if (dateArray?.length > 0 && dateArray?.includes(';')) {
            const vacationDatesArray = dateArray.split(';');
            let vacationDatesString = '';
            for (let i = 0; i < vacationDatesArray.length; i++) {
                if (i === 0) {
                    vacationDatesString = vacationDatesArray[0];
                } else if (i > 0) {
                    vacationDatesString = this.getRemainingDaysAfterFirstDate(
                        vacationDatesString,
                        vacationDatesArray,
                        i
                    );
                }
            }
            return vacationDatesString;
        }
        return dateArray;
    }

    getIsContractSigned(contractRequestId: number): Observable<boolean> {
        const url = `${this.coreApiPath}/contract-requests/${contractRequestId}/is-contract-signed`;
        return this.http.get<boolean>(url);
    }

    formatMessageBodyAsHtml(messageBody: string): string {
        if (!messageBody) {
            return '';
        }
        return messageBody.replace(/\r?\n/g, '<br/>').replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;');
    }

    private getRemainingDaysAfterFirstDate(
        vacationDatesString: string,
        vacationDatesArray: string[],
        index: number
    ): string {
        if (this.getNumberOfDaysBetween(vacationDatesArray[index - 1], vacationDatesArray[index]) > 1) {
            if (vacationDatesString.includes(vacationDatesArray[index - 1])) {
                vacationDatesString += `; ${vacationDatesArray[index]}`;
            } else {
                vacationDatesString += ` - ${vacationDatesArray[index - 1]}; ${vacationDatesArray[index]}`;
            }
        } else if (
            this.getNumberOfDaysBetween(vacationDatesArray[index - 1], vacationDatesArray[index]) === 1 &&
            index === vacationDatesArray.length - 1
        ) {
            vacationDatesString += ` - ${vacationDatesArray[index]}`;
        }
        return vacationDatesString;
    }

    private getNumberOfDaysBetween(firstDate: string, secondDate: string) {
        const oneDayInMilliseconds = 1000 * 60 * 60 * 24;
        const differenceInMillseconds = new Date(secondDate).getTime() - new Date(firstDate).getTime();
        return Math.round(differenceInMillseconds / oneDayInMilliseconds);
    }
}
